import { Field, getIn, useFormikContext } from 'formik';
import PropTypes from 'prop-types';
import { useMemo } from 'react';
import { useTheme } from 'styled-components';

import { AdditionalInformation } from '@hivebrite/design-system/AdditionalInformation';
import { Box } from '@hivebrite/design-system/Box';
import { Label } from '@hivebrite/design-system/Label';
import { ScreenReadersContent } from '@hivebrite/design-system/ScreenReadersContent';
import { POSITIONS } from '@hivebrite/design-system/Tooltip';
import { getErrorId, getTooltipId } from '@hivebrite/utils';

import getSelectComponent from 'hb-react/shared/components/formik/utils/getSelectComponent';
import { getOptionValue } from 'hb-react/shared/components/ReactSelect/utils/selectValues';

import { validateSelect } from './validate';

const Select = ({
  ariaDescribedBy,
  ariaLabel,
  disabled,
  getOptionValue,
  hideError,
  id,
  inputHtml,
  info = undefined, // to avoid adding @ts-expect-error for info={undefined} in very file using Select.jsx
  infoIcon = undefined, // to avoid adding @ts-expect-error for info={undefined} in very file using Select.jsx
  isClearable,
  isMulti = false,
  isNotSearchable,
  label,
  labelMultiline,
  menuPlacement,
  menuPortalTarget = undefined, // to avoid adding @ts-expect-error for menuPortalTarget={undefined} in every file using Select.jsx
  name,
  notCloseMenuOnSelect,
  onBlur,
  onChange,
  options,
  placeholder,
  tooltip,
  tooltipPosition,
  type,
  validate: { required },
  wrapperProps,
  autoComplete = false,
  menuShouldBlockScroll,
  labelPosition,
}) => {
  const {
    errors,
    values,
    touched,
    setFieldValue,
    setFieldTouched,
    submitCount,
  } = useFormikContext();
  const theme = useTheme();
  const SelectComponent = getSelectComponent(type);
  const htmlId = id || name;
  const formikValue = getIn(values, name);
  const formikError = getIn(errors, name);
  const formikTouched = getIn(touched, name);

  const tooltipId = useMemo(() => getTooltipId(htmlId), [htmlId]);
  const errorId = useMemo(() => getErrorId(htmlId), [htmlId]);

  const handleChange = (selectedOption) => {
    const newValue = selectedOption ? getOptionValue(selectedOption) : '';

    setFieldValue(name, newValue);

    if (onChange) {
      onChange(selectedOption);
    }
  };

  const handleBlur = () => {
    setFieldTouched(name, true);

    if (onBlur) {
      onBlur();
    }
  };

  return (
    <Box mb={6} display="block" {...wrapperProps}>
      <Box
        display="block"
        opacity={disabled ? theme.input.disabled.opacity : 1}
        {...(labelPosition === 'left' && {
          gridColumnGap: '8px',
          alignItems: 'center',
          display: 'flex',
        })}
      >
        {label && (
          <Label
            id={htmlId}
            disabled={disabled}
            multiline={labelMultiline}
            required={required}
            tooltip={tooltip}
            initialPosition={tooltipPosition}
          >
            {label}
          </Label>
        )}
        <Field
          name={name}
          validate={(value) =>
            validateSelect({
              required,
              value,
            })
          }
        >
          {() => (
            <SelectComponent
              ariaDescribedBy={ariaDescribedBy}
              // Note - describedById is not align with the rest of the App (should be ariaDescribedBy). To be updated
              {...((formikError || ariaDescribedBy || tooltip) && {
                describedById: [
                  formikError && errorId,
                  ariaDescribedBy,
                  tooltip && tooltipId,
                ]
                  .filter(Boolean)
                  .join(' '),
              })}
              ariaLabel={ariaLabel}
              disabled={disabled}
              error={(submitCount || formikTouched) && formikError}
              id={htmlId}
              isClearable={isClearable}
              isMulti={isMulti}
              isNotSearchable={isNotSearchable}
              name={name}
              menuPlacement={menuPlacement}
              menuPortalTarget={menuPortalTarget}
              notCloseMenuOnSelect={notCloseMenuOnSelect}
              onBlur={handleBlur}
              onChange={handleChange}
              options={options}
              placeholder={placeholder}
              value={formikValue}
              autoComplete={autoComplete}
              menuShouldBlockScroll={menuShouldBlockScroll}
              {...inputHtml}
            />
          )}
        </Field>
      </Box>

      {/* Make tooltip also available for ScreenReaders */}
      {tooltip && (
        <ScreenReadersContent id={tooltipId}>
          {typeof tooltip === 'function' ? tooltip() : tooltip}
        </ScreenReadersContent>
      )}

      {info && typeof info === 'string' && (
        <AdditionalInformation iconName={infoIcon} id={id} type="info">
          {info}
        </AdditionalInformation>
      )}

      {!hideError && (formikTouched || submitCount > 0) && formikError && (
        <AdditionalInformation type="error" id={htmlId}>
          {formikError}
        </AdditionalInformation>
      )}
    </Box>
  );
};

Select.propTypes = {
  name: PropTypes.string.isRequired,
  ariaDescribedBy: PropTypes.string,
  ariaLabel: PropTypes.string,
  disabled: PropTypes.bool,
  getOptionValue: PropTypes.func,
  hideError: PropTypes.bool,
  id: PropTypes.string,
  inputHtml: PropTypes.object,
  isClearable: PropTypes.bool,
  isNotSearchable: PropTypes.bool,
  label: PropTypes.string,
  labelMultiline: PropTypes.bool,
  menuPlacement: PropTypes.string,
  notCloseMenuOnSelect: PropTypes.bool,
  onBlur: PropTypes.func,
  onChange: PropTypes.func,
  options: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  placeholder: PropTypes.string,
  tooltip: PropTypes.string,
  type: PropTypes.oneOf([
    'codeAPE',
    'companies',
    'countries',
    'default',
    'industries',
    'job_functions',
    'skills',
    'timezone',
    'users',
    'mentors',
    'mentees',
  ]),
  validate: PropTypes.shape({
    required: PropTypes.bool,
  }),
  wrapperProps: PropTypes.object,
  autocomplete: PropTypes.bool,
  menuShouldBlockScroll: PropTypes.bool,
  labelPosition: PropTypes.oneOf(['left', 'top']),
};

Select.defaultProps = {
  getOptionValue,
  hideError: false,
  inputHtml: {},
  labelMultiline: true,
  menuPlacement: 'auto',
  tooltipPosition: POSITIONS.BOTTOM,
  type: 'default',
  validate: {},
  wrapperProps: {},
  autocomplete: false,
  menuShouldBlockScroll: false,
};

export default Select;
